import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import type { RootState } from "../../../store/store";
import { FREELANCER_LOGIN_PAGE } from "../../auth/routes";
import { freelancerStoreSlotPath } from "../routes";

import { getAvailableStoresPaginatedApi, StorePublic } from "../../store/storeApi";
import { clearFreelancerSession } from "../../../store/freelancerAuthSlice";

import DenseForest from "../../../img/dense-forest.jpg";

const FreelancerStoresPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const token = useSelector((state: RootState) => state.freelancerAuth.accessToken);

  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState<StorePublic[]>([]);

  const hasToken = useMemo(() => Boolean(token), [token]);

  const fetchStores = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const data = await getAvailableStoresPaginatedApi(token, 1, 100);
      setStores(data.items ?? []);
    } catch (err: any) {
      const msg = err?.message ?? "Erro ao listar lojas.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!hasToken) {
      toast.error("Você precisa estar logado.");
      navigate(FREELANCER_LOGIN_PAGE(), { replace: true, state: { from: location.pathname } });
      return;
    }
    fetchStores();
  }, [hasToken, navigate, fetchStores, location.pathname]);

  const handleLogout = () => {
    dispatch(clearFreelancerSession());
    toast.success("Você saiu.");
    navigate(FREELANCER_LOGIN_PAGE(), { replace: true });
  };

  if (!hasToken) return null;

  return (
    <div className="StoreMenuPage SlotsManagerPage ecosystem">
      <img className="ecosystem__bg-img" src={DenseForest} alt="" aria-hidden="true" />
      <div className="ecosystem__content-bg" aria-hidden="true" />

      <div className="freelancer-container ecosystem__content">
        <header className="ecosystem__header">
          <h1 className="ecosystem__title">
            <span className="ecosystem__title-strong">Lojas com </span>
            <span className="ecosystem__title-light">Horários Disponíveis</span>
          </h1>

          <p className="ecosystem__subtitle">Selecione uma loja para ver os horários disponíveis</p>

          <div className="ecosystem__actions">
            <button className="ecosystem__actionBtn" onClick={fetchStores} disabled={loading}>
              {loading ? "Atualizando..." : "Atualizar"}
            </button>

            <button className="ecosystem__actionBtn" onClick={() => navigate("/")} disabled={loading}>
              Home
            </button>
          </div>
        </header>

        <div className="freelancer-card-table">
          <div className="Box">
            <div className="SlotsTitleRow">
              <div className="BoxTitle" style={{ margin: 0 }}>
                Lojas com horários disponíveis
              </div>
            </div>

            {loading ? (
              <div className="BoxEmpty">Carregando lojas...</div>
            ) : stores.length === 0 ? (
              <div className="BoxEmpty">Nenhuma loja com horários disponíveis no momento.</div>
            ) : (
              <div className="List">
                {stores.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="ListItem"
                    onClick={() => navigate(freelancerStoreSlotPath(s.id))}
                  >
                    <div className="ListItemTitle">{s.nomeFantasia}</div>
                    <div className="ListItemSub">{s.endereco ? s.endereco : "Endereço não informado"}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreelancerStoresPage;
